import type { configType } from "~/js/network/network-handler";
import type { IFileType } from "../fileHelpers";
import type { IIntLimitsAccessGroup } from "../validationHelpers";
import type { optionaConstructor } from "./ISockets";

export enum NodeVisibility {
	Invisible = "invisible",
	Old = "old",
	Visible = "visible",
	SuperUser = "superuser"
}

export type INode = {
	node: { nodeDefinition: INodeDefinition };
};
export type INodePosition = {
	x: number;
	y: number;
};

/**
 * @description Connection saved to workspace from output node to input
 */
export type Connection = {
	from: {
		nodeUuid: string;
		/** socket name */
		name: string;
		/** socket type */
		type: string;
	};
	to: {
		nodeUuid: string;
		/** socket name */
		name: string;
		/** socket type */
		type: string;
	};
};

/**
 * Node information saved to workspaces
 */
export type IUserTemplateParams = {
	/** service static identifier, number globalId was stored is in legacy nodes such as import files */
	globalId: string | number;
	/** service name */
	name: string;
	/** service id */
	id: number;
	taskID?: number;
	meta: Record<string, any>;
	uuid?: string;
	inputSocketGroup: Record<string, IUniversalTaskSocket> | IUniversalTaskSocket[];
	outputSocketGroup: Array<IWorkspaceFileSocket>;
	params: Record<string, IUniversalTaskSocket> | IUniversalTaskSocket[];
	width?: number;
	height?: number;
	position?: INodePosition;
	connections?: Connection[];
	/**legacy node hint sticker */
	sticker?: {
		position: INodePosition;
		visible: boolean;
	};
};
export type ITaskSocket =
	| {
			name: string;
			type: "array";
			value: "";
			id: number[];
	  }
	| ({
			type: Exclude<IDataType, "array">;
			id: number;
	  } & Omit<IUniversalTaskSocket, "type">);

export type IWorkspaceFileSocket = {
	name: string;
	type: IDataType;
	optionalInfo?: Record<string, any>;
	value: { fileId: number } | { fileId: number }[] | IFileType | IFileType[];
};
export type UserTemplateParamsHistory = {
	/**
	 * nodeId
	 */
	id: number;
	globalId: string;
	name: string;
	inputs: Record<string, IUniversalTaskSocket> | IUniversalTaskSocket[];
	outputs: Record<string, ITaskSocket> | ITaskSocket[];
	params: Record<string, IUniversalTaskSocket> | IUniversalTaskSocket[];
	metadata?: {
		width?: number;
		position?: INodePosition;
	};
};

export type INodeDefinition = INodeSockets & INodeDefinitionConfig;

export type INodeSockets = {
	inputs: Array<IUniversalSocketType>;
	params: Array<IUniversalSocketType>;
	outputs: Array<IUniversalSocketType>;
};

export type ICalculateCostDefinition = {
	id: number;
	inputs: Array<Pick<IUniversalSocketType, "name" | "value">>;
	params: Array<Pick<IUniversalSocketType, "name" | "value">>;
	outputs: Array<Pick<IUniversalSocketType, "name" | "value">>;
};

export type INodeStartTrainingSchema = {
	id: number;
} & INodeSocketsDto;
export type INodeSocketsDto = {
	inputs: Array<IUniversalTaskSocket>;
	params: Array<IUniversalTaskSocket>;
	outputs: Array<IUniversalTaskSocket>;
};
export type INodeSocketsExtended = {
	inputs: Array<IUniversalSocketTypeExtended>;
	params: Array<IUniversalSocketTypeExtended>;
	outputs: Array<IUniversalSocketTypeExtended>;
};
export type INodeDefinitionExtended = INodeSocketsExtended & {
	allSockets: Record<string, IUniversalSocketTypeExtended>;
	groupedSockets: groupedSocketUnion<IUniversalSocketType>;
} & INodeDefinitionConfig;

export type INodeDefinitionConfig = {
	averageTimeInSeconds: number;
	description: string;
	id: number;
	id_global: string;
	name: string;
	/**
	 * flag from airtable used to hide node from sidebar
	 */
	isVisible: NodeVisibility;
	/**
	 * flag that determines is node available to user
	 */
	isAvailable: boolean;
	defaultPrice: number;
};
export type IInputOutputSocketDtoUnion = IArraySocketDto | IImageSocketDto | IParamSocketDtoUnion;
export type IParamSocketDtoUnion =
	| INumberSocketDto
	| ITextSocketDto
	| IEnumSocketDto
	| IAudioSocketDto;
export type IInputOutputSocketUnion = ISocketUnion;
export type IParamSocketUnion = ISocketUnion;
export type ISocketUnion =
	| IArraySocket
	| IImageSocket
	| INumberSocket
	| ITextSocket
	| IEnumSocket
	| IBoolSocket
	| IAudioSocket;
export type IInputOutputSocketUnionExtended =
	| ISocketExtended<IArraySocket>
	| ISocketExtended<IImageSocket>
	| ISocketExtended<IParamSocketUnion>
	| ISocketExtended<IAudioSocket>;
export type IParamSocketUnionExtended =
	| ISocketExtended<INumberSocket>
	| ISocketExtended<ITextSocket>
	| ISocketExtended<IEnumSocket>
	| ISocketExtended<IBoolSocket>
	| ISocketExtended<IAudioSocket>;
export type ISocketExtended<SocketType extends ISocketUnion> = SocketType & ISocketMergeItem;

export type SocketMetaData = {
	dimensions?: { height: number; width: number } | { height: number; width: number }[];
};

export type VideoMetaData = {
	duration: number;
	width: number;
	height: number;
};

export type VideoSocketMetaData = {
	duration?: VideoMetaData | VideoMetaData[];
};

export type AudioSocketMetaData = {
	duration?: number | number[];
};

export type IUniversalTaskSocket = Omit<IUniversalSocketType, "optional" | "value" | "dataType"> & {
	isFixedSeed?: boolean;
	type: IUniversalSocketType["dataType"];
	value: IUniversalSocketType["value"] | null;
	optional?: boolean;
	isModified?: boolean;
	/** Optional metadata, e.g., image dimensions */
	meta?: SocketMetaData;
};
export type IUniversalSocketTypeExtended = IUniversalSocketType & ISocketMergeItem;
export type IUniversalSocketType = {
	name: string;
	optional?: boolean;
	isHidden?: boolean;
	dataType: IDataType;
	value?: IDataTypeTypes | Array<IDataTypeTypes>;
} & Partial<INumberOptions | IArrayOptions | IEnumOptions>;

export type ISocketDto<ISocket extends ISocketUnion> = Omit<
	ISocket,
	"dataType" | "options" | "optional"
> & {
	type: ISocket["dataType"];
	optional: boolean;
};

export type IImageSocketDto = ISocketDto<IImageSocket>;
export type IArraySocketDto = ISocketDto<IArraySocket>;
export type INumberSocketDto = ISocketDto<INumberSocket>;
export type ITextSocketDto = ISocketDto<ITextSocket>;
export type IEnumSocketDto = ISocketDto<IEnumSocket>;
export type IBoolSocketDto = ISocketDto<IBoolSocket>;
export type IAudioSocketDto = ISocketDto<IAudioSocket>;
export type IImageSocket = {
	name: string;

	dataType: "image";
} & optionaConstructor<number>;

export type IArraySocket = {
	name: string;
	dataType: "array";
	options: IArrayOptions;
} & optionaConstructor<number[]>;
export type INumberSocket = {
	name: string;
	dataType: "number";
	options: INumberOptions;
} & optionaConstructor<number>;
export type ITextSocket = {
	name: string;
	dataType: "text";
} & optionaConstructor<string>;
export type IEnumSocket = {
	name: string;

	dataType: "enum";
	options: IEnumOptions;
} & optionaConstructor<string>;
export type IBoolSocket = {
	name: string;
	dataType: "bool";
} & optionaConstructor<boolean>;

export type IAudioSocket = {
	name: string;
	dataType: "audio";
} & optionaConstructor<boolean>;

export type INumberOptions = {
	max: number | null;
	min: number | null;
	random: boolean | null;
	step: number | null;
};
export type IArrayOptions = {
	minItems: number;
	maxItems: number;
	itemType: {
		dataType: IDataType;
	};
};
export type IModifierRecord = Record<string, IModifier>;
export type IModifier = {
	value: number | boolean | string;
	isHidden?: boolean;
	isOptional?: boolean;
	isEnabled?: boolean;
};
export type IEnumImgPickerVariantValue = {
	value: string;
	name: string;
	description?: string;
	imgUrl: string;
	modifiers?: IModifierRecord;
};
export type IEnumValuesUnion =
	| { value: string; name: string; modifiers?: IModifierRecord }
	| string;

export type IEnumOptions = {
	values: Array<IEnumValuesUnion>;
};
export type IDataTypeTypes = string | number | boolean | null | IFileType;
export type IDataType =
	| "image"
	| "image_svg"
	| "array"
	| "audio"
	| "video"
	| "object3d"
	| "object3d_obj"
	| "binary"
	| "trigger"
	| IParamsDataTypes;
export type IParamsDataTypes = "number" | "text" | "enum" | "bool";

export type MediaSocketType = "image" | "video" | "audio";

type SocketsDisplayKey = `Sockets_JSON_${configType["GRAYLOG_BUILD_NAME"]}`;
export type ISocketMergeItemBase = {
	displayName?: string;
	description?: string;
	limits?: ISocketLimits;
	validateSimilar?: ISocketValidateSimilar;
	isNeedToMoveFromSettings?: boolean;

	group?: string;
	isHidden?: boolean;
	intLimitsAccessGroup?: IIntLimitsAccessGroup;
};

export type groupedSocketUnion<socketType> = Array<
	{ groupName: string; items: groupedSocketUnion<socketType> } | socketType
>;
export type IEnumMergeItem =
	| {
			dataType?: "enum";
			variant: "imgPicker";
			options: {
				values: IEnumImgPickerVariantValue[];
			};
	  }
	| {
			dataType?: "enum";

			options: IEnumOptions;
	  }
	| {
			dataType?: "enum";
			variant: "toggleGroup";
			options: IEnumOptions;
	  };
export type INumberMergeItem =
	| {
			dataType?: "number";
			variant: "slider";
			options: INumberOptions;
	  }
	| {
			dataType?: "number";

			options: INumberOptions;
	  };
export type IMergedCustomSocket<
	Socket extends ISocketUnion,
	MergeItem extends ISocketMergeItemUnion
> = Socket & MergeItem & ISocketMergeItemBase;
export type ISocketMergeItemUnion =
	| IEnumMergeItem
	| INumberMergeItem
	| {
			dataType?: IDataType;
			options?: IArrayOptions;
	  };
export type ISocketMergeItem = ISocketMergeItemUnion & ISocketMergeItemBase;

export type EnumSocketsVariants = "imgPicker" | "toggleGroup";
export type NumberSocketVariants = "slider";

export type ISocketLimits = {
	hightLimit: number;
	widthLimit: number;
};
export type ISocketValidateSimilar = {
	errorMessage?: string;
	sockets: string[];
};
export type ISocketDisplayMerge = Record<string, ISocketMergeItem>;

export type IAirtableMergeItem = {
	GlobalID: string;
	DisplayName: string;
	imageValidationRules: string;
} & {
	[key in SocketsDisplayKey]: string;
};
export type imageValidationRules = {
	imageMinCount: number;
	imageMaxCount: number;
	imageMaxHeight: number;
	imageMaxWidth: number;
	imagePreferHeight: number;
	imagePreferWidth: number;
};
export type IMergeItem = {
	globalId: string;
	socketDisplayMerge: ISocketDisplayMerge;
	displayName: string;
};
export type IExtendedSocket = ISocketMergeItem & IInputOutputSocketUnion;
